import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Label } from '../components/ui/label';
import { TrendingUp, MessageSquare, Heart, Clock, User, Search, Plus, Menu, ChevronRight, Filter } from 'lucide-react';
import { fetchApi, formatTimeAgo, cn } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export default function ForumPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, user } = useAuth();
  
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState('recent');
  const [tickerFilter, setTickerFilter] = useState(searchParams.get('ticker') || '');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Create post dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newPost, setNewPost] = useState({ title: '', content: '', ticker: '' });
  const [creating, setCreating] = useState(false);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('sort', sortBy);
      params.set('limit', '20');
      if (tickerFilter) params.set('ticker', tickerFilter);
      
      const data = await fetchApi(`/forum/posts?${params.toString()}`);
      setPosts(data.posts || []);
      setTotal(data.total || 0);
    } catch (error) {
      toast.error('Failed to load forum posts');
    } finally {
      setLoading(false);
    }
  }, [sortBy, tickerFilter]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  useEffect(() => {
    // Pre-fill ticker from URL params
    const ticker = searchParams.get('ticker');
    if (ticker) {
      setTickerFilter(ticker);
      setNewPost(prev => ({ ...prev, ticker }));
    }
  }, [searchParams]);

  // Validate English-only text
  const isEnglishOnly = (text) => {
    const pattern = /^[a-zA-Z0-9\s\.\,\!\?\'\"\-\:\;\(\)\[\]\@\#\$\%\&\*\+\=\/\\\n\r]*$/;
    return pattern.test(text);
  };

  const handleCreatePost = async () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to create a post');
      navigate('/auth');
      return;
    }
    
    if (!newPost.title.trim()) {
      toast.error('Please enter a title for your post');
      return;
    }
    
    if (!newPost.content.trim()) {
      toast.error('Please enter content for your post');
      return;
    }
    
    if (newPost.title.length > 200) {
      toast.error('Title must be 200 characters or less');
      return;
    }
    
    if (newPost.content.length > 5000) {
      toast.error('Content must be 5000 characters or less');
      return;
    }
    
    if (!isEnglishOnly(newPost.title)) {
      toast.error('Title must contain only English characters');
      return;
    }
    
    if (!isEnglishOnly(newPost.content)) {
      toast.error('Content must contain only English characters');
      return;
    }
    
    setCreating(true);
    try {
      const data = await fetchApi('/forum/posts', {
        method: 'POST',
        body: JSON.stringify({
          title: newPost.title,
          content: newPost.content,
          ticker: newPost.ticker || null
        })
      });
      toast.success('Post created successfully!');
      setCreateDialogOpen(false);
      setNewPost({ title: '', content: '', ticker: '' });
      fetchPosts();
    } catch (error) {
      toast.error(error.message || 'Failed to create post. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const handleLike = async (postId, e) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      toast.error('Please sign in to like posts');
      return;
    }
    
    try {
      const data = await fetchApi(`/forum/posts/${postId}/like`, { method: 'POST' });
      setPosts(posts.map(post => 
        post.post_id === postId 
          ? { ...post, likes_count: data.likes_count, liked_by: data.action === 'liked' 
              ? [...(post.liked_by || []), user?.user_id] 
              : (post.liked_by || []).filter(id => id !== user?.user_id) }
          : post
      ));
    } catch (error) {
      toast.error('Failed to like post');
    }
  };

  const clearFilter = () => {
    setTickerFilter('');
    setSearchParams({});
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <nav className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-sm bg-primary flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-heading font-bold text-lg sm:text-xl">MarketMind</span>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4">
            <Link to="/stocks" className="text-muted-foreground hover:text-foreground transition-colors">
              Stocks
            </Link>
            <Link to="/search" className="text-muted-foreground hover:text-foreground transition-colors">
              Advanced Search
            </Link>
            {isAuthenticated ? (
              <Button onClick={() => navigate('/dashboard')}>Dashboard</Button>
            ) : (
              <Button onClick={() => navigate('/auth')}>Sign In</Button>
            )}
          </div>

          {/* Mobile Menu */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px] bg-card">
              <SheetHeader>
                <SheetTitle className="text-left">Menu</SheetTitle>
              </SheetHeader>
              <div className="flex flex-col gap-4 mt-6">
                <Link to="/stocks" onClick={() => setMobileMenuOpen(false)} className="py-2">Stocks</Link>
                <Link to="/search" onClick={() => setMobileMenuOpen(false)} className="py-2">Advanced Search</Link>
                {isAuthenticated ? (
                  <Button onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }}>Dashboard</Button>
                ) : (
                  <Button onClick={() => { navigate('/auth'); setMobileMenuOpen(false); }}>Sign In</Button>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight mb-1">
              Community Forum
            </h1>
            <p className="text-sm text-muted-foreground">
              {total} discussion{total !== 1 ? 's' : ''} {tickerFilter && `about ${tickerFilter}`}
            </p>
          </div>
          
          {isAuthenticated ? (
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="create-post-btn" className="w-full sm:w-auto">
                  <Plus className="w-4 h-4 mr-2" />
                  New Post
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>Create Discussion</DialogTitle>
                  <DialogDescription>Share your thoughts with the community</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Title</Label>
                    <Input
                      data-testid="post-title-input"
                      placeholder="What's on your mind?"
                      value={newPost.title}
                      onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                    />
                </div>
                <div className="space-y-2">
                  <Label>Stock Ticker (optional)</Label>
                  <Input
                    data-testid="post-ticker-input"
                    placeholder="e.g., AAPL"
                    value={newPost.ticker}
                    onChange={(e) => setNewPost({ ...newPost, ticker: e.target.value.toUpperCase() })}
                    className="font-mono"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Content</Label>
                  <Textarea
                    data-testid="post-content-input"
                    placeholder="Share your analysis, prediction, or question..."
                    value={newPost.content}
                    onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                    rows={5}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
                <Button data-testid="submit-post-btn" onClick={handleCreatePost} disabled={creating}>
                  {creating ? 'Creating...' : 'Post'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          ) : (
            <Button 
              data-testid="create-post-btn" 
              className="w-full sm:w-auto"
              onClick={() => {
                toast.error('Please sign in to create a post');
                navigate('/auth');
              }}
            >
              <Plus className="w-4 h-4 mr-2" />
              New Post
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              data-testid="ticker-filter-input"
              placeholder="Filter by ticker (e.g., AAPL)"
              value={tickerFilter}
              onChange={(e) => setTickerFilter(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === 'Enter' && fetchPosts()}
              className="pl-9 font-mono"
            />
          </div>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger data-testid="sort-select" className="w-full sm:w-40">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="recent">Most Recent</SelectItem>
              <SelectItem value="popular">Most Liked</SelectItem>
              <SelectItem value="comments">Most Comments</SelectItem>
            </SelectContent>
          </Select>
          {tickerFilter && (
            <Button variant="outline" onClick={clearFilter} className="shrink-0">
              Clear Filter
            </Button>
          )}
        </div>

        {/* Posts List */}
        <div className="space-y-4">
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <Card key={i} className="bg-card border-border/50">
                <CardContent className="p-4">
                  <Skeleton className="h-6 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-4 w-1/2" />
                </CardContent>
              </Card>
            ))
          ) : posts.length === 0 ? (
            <Card className="bg-card border-border/50">
              <CardContent className="p-8 text-center">
                <MessageSquare className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground mb-4">
                  {tickerFilter ? `No discussions about ${tickerFilter} yet` : 'No discussions yet'}
                </p>
                <Button onClick={() => setCreateDialogOpen(true)}>Start a Discussion</Button>
              </CardContent>
            </Card>
          ) : (
            posts.map((post) => (
              <Card 
                key={post.post_id} 
                className="bg-card border-border/50 hover:border-primary/50 transition-colors cursor-pointer"
                onClick={() => navigate(`/forum/${post.post_id}`)}
                data-testid={`forum-post-${post.post_id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        {post.ticker && (
                          <Badge 
                            variant="outline" 
                            className="font-mono text-xs cursor-pointer hover:bg-primary/10"
                            onClick={(e) => { e.stopPropagation(); navigate(`/stocks/${post.ticker}`); }}
                          >
                            ${post.ticker}
                          </Badge>
                        )}
                      </div>
                      <h3 className="font-semibold text-base sm:text-lg leading-snug line-clamp-2">
                        {post.title}
                      </h3>
                    </div>
                  </div>
                  
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                    {post.content}
                  </p>
                  
                  <div className="flex items-center justify-between text-xs sm:text-sm text-muted-foreground">
                    <div className="flex items-center gap-3 sm:gap-4">
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {post.author_name}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatTimeAgo(post.created_at)}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <button 
                        className={cn(
                          "flex items-center gap-1 hover:text-red-500 transition-colors",
                          post.liked_by?.includes(user?.user_id) && "text-red-500"
                        )}
                        onClick={(e) => handleLike(post.post_id, e)}
                      >
                        <Heart className={cn("w-4 h-4", post.liked_by?.includes(user?.user_id) && "fill-current")} />
                        {post.likes_count || 0}
                      </button>
                      <span className="flex items-center gap-1">
                        <MessageSquare className="w-4 h-4" />
                        {post.comments_count || 0}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
